/*
 * Copyright (c) Nexign, JSC, 2024.
 */
/**
 *
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.30.0
 */

import { Fragment, useCallback, useEffect } from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { RadioButton } from '@/main/components/common';
import { FlexStyled } from '@/main/components/common/Block/Flexbox';
import { LayoutStyled } from '@/main/components/common/Block/Layout';
import DatePicker from '@/main/components/common/DatePicker/DatePicker';
import { FormBlockStyled } from '@/main/components/common/FormStyled/FormStyled';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardCollationReport,
} from '@/main/modules/internationalization/intSelectors';
import { ICommonTranslation } from '@/main/modules/internationalization/types';
import { IWizardCollationReportTranslation } from '@/main/modules/internationalization/types/wizards/collationReport';
import { IRootReducer } from '@/main/reducers/rootReducer';

import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { TWizardCollationReportSetParamsPayload } from '../types/wizardCollationReportActionTypes';
import { wizardCollationReportSetParamsAction } from '../wizardCollationReportActions';
import { EWizardCollationReportSteps } from '../wizardCollationReportConstant';
import { IWizardCollationReportReducer } from '../wizardCollationReportReducer';
import {
    makeSelectWizardCollationReportIsStepValid,
    makeSelectWizardCollationReportParams,
} from '../wizardCollationReportSelectors';
import {
    WizardCollationReportPeriodStyled,
    WizardCollationReportStepChooseStyled,
} from './styled/WizardCollationReportStyled';

const WizardCollationReportStepChoose = ({
    wizardCollationReportState,
    translation,
    translationCommon,
    setParams,
}: IConnectedProps & IDispatchProps) => {
    const stepTranslation = translation[EWizardCollationReportSteps.ChooseStep];

    const setStartDateHandler = useCallback(
        (date: Moment) => {
            setParams({
                startDate: date.format(EFormatData.ReverseData),
            });
        },
        [setParams],
    );

    const setEndDateHandler = useCallback(
        (date: Moment) => {
            setParams({
                endDate: date.format(EFormatData.ReverseData),
            });
        },
        [setParams],
    );

    const setReportFormat = (format: string) => {
        setParams({
            reportFormat: format,
        });
    };

    useEffect(() => {
        const from = moment(wizardCollationReportState.startDate);
        const to = moment(wizardCollationReportState.endDate);
        if (wizardCollationReportState.startDate && wizardCollationReportState.endDate && from.unix() >= to.unix()) {
            setParams({
                endDate: from.format(EFormatData.FullDataWithTimeZone),
            });
        }
    }, [setParams, wizardCollationReportState.endDate, wizardCollationReportState.startDate]);

    return (
        <WizardCollationReportStepChooseStyled>
            <div className="wizard-collation-report__title">{stepTranslation.chooseParams}</div>

            <div className="wizard-collation-report__subtitle">{stepTranslation.startDateTitle}</div>
            <WizardCollationReportPeriodStyled>
                <DatePicker
                    dataName="wizard-collation-report__start-date-picker"
                    maxDate={moment()}
                    minDate={moment().subtract(5, 'years')}
                    selectedDate={moment(wizardCollationReportState.startDate)}
                    translation={translationCommon}
                    onSelectDate={setStartDateHandler}
                />
            </WizardCollationReportPeriodStyled>

            <div className="wizard-collation-report__subtitle">{stepTranslation.endDateTitle}</div>
            <WizardCollationReportPeriodStyled>
                <DatePicker
                    dataName="wizard-collation-report__end-date-picker"
                    maxDate={moment()}
                    minDate={
                        wizardCollationReportState.startDate
                            ? moment(wizardCollationReportState.startDate)
                            : moment().subtract(5, 'years')
                    }
                    selectedDate={moment(wizardCollationReportState.endDate)}
                    translation={translationCommon}
                    onSelectDate={setEndDateHandler}
                />
            </WizardCollationReportPeriodStyled>

            <FormBlockStyled>
                <div className="wizard-collation-report__title">Формат отчета</div>

                <FlexStyled>
                    {wizardCollationReportState.formatsAvailable.map((format) => (
                        <Fragment key={format}>
                            <LayoutStyled
                                className="mr-4"
                                data-container="calls-detail-report-format"
                                onClick={() => {
                                    setReportFormat(format);
                                }}
                            >
                                <RadioButton
                                    checked={wizardCollationReportState.reportFormat === format}
                                    dataName={`wizard-collation-report__radio-format_${format.toLowerCase()}`}
                                    label={format}
                                />
                            </LayoutStyled>
                        </Fragment>
                    ))}
                </FlexStyled>
            </FormBlockStyled>
        </WizardCollationReportStepChooseStyled>
    );
};

export interface IConnectedProps {
    isStepValid: boolean;
    wizardCollationReportState: IWizardCollationReportReducer;
    translation: IWizardCollationReportTranslation;
    translationCommon: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isStepValid: makeSelectWizardCollationReportIsStepValid(),
    wizardCollationReportState: makeSelectWizardCollationReportParams(),
    translation: makeSelectTranslationWizardCollationReport(),
    translationCommon: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    setParams: (payload: TWizardCollationReportSetParamsPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setParams: (payload) => dispatch(wizardCollationReportSetParamsAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardCollationReportStepChoose);
